const DEFAULT_INCLUSIVE = [false, false, false, false];

export function isMissingValue(value) {
  return value === null || value === undefined || value === '';
}

function crosses(value, threshold, direction, inclusive) {
  if (direction === 'lower') {
    return inclusive ? value <= threshold : value < threshold;
  }
  return inclusive ? value >= threshold : value > threshold;
}

export function scoreIndicator(value, indicator) {
  if (indicator?.cepScoresFive && value === 'CEP') return 5;
  if (isMissingValue(value)) return null;

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;

  const [b5, b4, b3, b2] = indicator.breakpoints;
  const inclusive = indicator.inclusive || DEFAULT_INCLUSIVE;
  const direction = indicator.direction;

  if (crosses(numeric, b5, direction, inclusive[0])) return 5;
  if (crosses(numeric, b4, direction, inclusive[1])) return 4 + (numeric - b4) / (b5 - b4);
  if (crosses(numeric, b3, direction, inclusive[2])) return 3 + (numeric - b3) / (b4 - b3);
  if (crosses(numeric, b2, direction, inclusive[3])) return 2 + (numeric - b2) / (b3 - b2);

  if (indicator.floorMode === 'proportional') {
    return b2 === 0 ? 1 : 1 + numeric / b2;
  }
  if (indicator.floorMode === 'capped') {
    const denom = indicator.cap - b2;
    return denom === 0 ? 1 : 1 + (indicator.cap - numeric) / denom;
  }
  return 1;
}

function indicatorWithBreakpoints(indicator, breakpointsById) {
  const override = breakpointsById?.[indicator.id];
  if (!override) return indicator;
  return { ...indicator, breakpoints: override };
}

export function scoreEntity(entity, indicators, weights, options = {}) {
  const { renormalize = false, breakpointsById = {} } = options;

  const contributions = indicators.map((baseIndicator) => {
    const indicator = indicatorWithBreakpoints(baseIndicator, breakpointsById);
    const weight = Number(weights?.[indicator.id] ?? indicator.defaultWeight) || 0;
    const value = entity?.[indicator.sourceField];
    const score = scoreIndicator(value, indicator);
    const missing = score === null;
    return {
      id: indicator.id,
      label: indicator.label,
      shortLabel: indicator.shortLabel,
      category: indicator.category,
      format: indicator.format,
      placeholder: Boolean(indicator.placeholder),
      value: isMissingValue(value) ? null : value,
      score,
      weight,
      effectiveWeight: missing ? 0 : weight,
      weighted: missing ? 0 : score * weight,
      missing,
    };
  });

  if (renormalize) {
    const availableWeight = contributions
      .filter((item) => !item.missing)
      .reduce((sum, item) => sum + item.weight, 0);
    if (availableWeight > 0) {
      contributions.forEach((item) => {
        if (item.missing) {
          item.effectiveWeight = 0;
          item.weighted = 0;
          return;
        }
        item.effectiveWeight = item.weight / availableWeight;
        item.weighted = item.score * item.effectiveWeight;
      });
    }
  }

  const total = contributions.reduce((sum, item) => sum + item.weighted, 0);
  const scored = contributions.filter((item) => !item.missing).length;

  return {
    total,
    contributions,
    coverage: {
      scored,
      total: indicators.length,
      missingWeight: contributions
        .filter((item) => item.missing)
        .reduce((sum, item) => sum + item.weight, 0),
    },
  };
}

export function rankEntities(entities, indicators, weights, options = {}) {
  const minEnrollment = Number(options.minEnrollment) || 0;
  const enrollmentField = options.enrollmentField || 'k12Enrollment2025_2026';

  return (entities || [])
    .filter((entity) => (Number(entity?.[enrollmentField]) || 0) >= minEnrollment)
    .map((entity) => ({
      entity,
      ...scoreEntity(entity, indicators, weights, options),
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return String(a.entity?.name || '').localeCompare(String(b.entity?.name || ''));
    })
    .map((row, index) => ({ ...row, rank: index + 1 }));
}
